// Notification types for TCO Admin Panel v2.0
// IMPORTANT: These must match backend enum exactly!

export enum NotificationType {
  GENERAL = 'general',
  REMINDER = 'reminder',
  DOCUMENTS = 'documents',
  REQUESTS = 'requests',
}

export enum NotificationChannel {
  PUSH = 'push',
  EMAIL = 'email',
  SMS = 'sms',
}

export enum NotificationPriority {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
  URGENT = 'urgent',
}

export enum DeliveryStatus {
  PENDING = 'pending',
  SENT = 'sent',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  READ = 'read',
}

export interface Notification {
  id: string
  tenant_id: string
  type: NotificationType
  title: string
  body: string
  data?: Record<string, any>
  priority: NotificationPriority
  channels: NotificationChannel[]

  // Recipients
  recipient_user_id?: string | null
  recipient_employee_id?: string | null
  recipient_role_id?: string | null
  recipient_department_id?: string | null
  is_broadcast: boolean

  // Scheduling
  scheduled_for?: string | null
  expires_at?: string | null

  // Status
  is_sent: boolean
  sent_at?: string | null

  // Metadata
  created_by_user_id?: string | null
  created_at: string
  updated_at: string

  // Relations (для списка с джойнами)
  recipient_user?: {
    id: string
    email: string
  } | null
  recipient_employee?: {
    id: string
    first_name: string
    last_name: string
  } | null
  created_by?: {
    id: string
    email: string
  } | null
}

export interface NotificationDelivery {
  id: string
  notification_id: string
  user_id: string
  employee_id?: string | null
  channel: NotificationChannel
  status: DeliveryStatus
  delivered_at?: string | null
  read_at?: string | null
  error_message?: string | null
  retry_count: number
  created_at: string
  updated_at: string

  // Relations
  user?: {
    id: string
    email: string
  }
  employee?: {
    id: string
    first_name: string
    last_name: string
  } | null
}

export interface DeviceToken {
  id: string
  user_id: string
  employee_id?: string | null
  device_id: string
  fcm_token: string
  platform: 'ios' | 'android' | 'web'
  app_version?: string | null
  is_active: boolean
  last_used_at?: string | null
  created_at: string
  updated_at: string

  // Relations
  user?: {
    id: string
    email: string
  }
  employee?: {
    id: string
    first_name: string
    last_name: string
  } | null
}

// Request/Response DTOs

export enum RecipientType {
  SINGLE = 'single',
  GROUP = 'group',
  ALL = 'all',
}

export interface CreateNotificationDto {
  type: NotificationType
  title: string
  message: string // Changed from 'body' to match backend
  data?: Record<string, any>

  // Recipient configuration (matches backend DTO)
  recipient_type: RecipientType // Required field
  recipient_ids?: string[] // Array of employee UUIDs for single/group
  department_id?: string // Department UUID for group notifications

  // Scheduling
  scheduled_at?: string // Changed from 'scheduled_for' to match backend

  // DEPRECATED FIELDS (kept for backwards compatibility, not sent to backend)
  /** @deprecated Use message instead */
  body?: string
  /** @deprecated Not supported by backend yet */
  priority?: NotificationPriority
  /** @deprecated Not supported by backend yet */
  channels?: NotificationChannel[]
  /** @deprecated Use recipient_type: 'all' instead */
  is_broadcast?: boolean
  /** @deprecated Use recipient_ids instead */
  recipient_employee_id?: string
  /** @deprecated Use recipient_ids instead */
  recipient_role_id?: string
  /** @deprecated Use department_id instead */
  recipient_department_id?: string
  /** @deprecated Use scheduled_at instead */
  scheduled_for?: string
  /** @deprecated Not supported by backend yet */
  expires_at?: string
}

export interface UpdateNotificationDto {
  title?: string
  body?: string
  data?: Record<string, any>
  priority?: NotificationPriority
  channels?: NotificationChannel[]
  scheduled_for?: string
  expires_at?: string
}

export interface NotificationFilters {
  type?: NotificationType
  priority?: NotificationPriority
  is_sent?: boolean
  is_broadcast?: boolean
  recipient_user_id?: string
  recipient_employee_id?: string
  recipient_role_id?: string
  recipient_department_id?: string
  created_by_user_id?: string
  from_date?: string
  to_date?: string
  page?: number
  limit?: number
  sort?: string
}

export interface DeliveryStatsResponse {
  notification_id: string
  total_deliveries: number
  by_status: {
    pending: number
    sent: number
    delivered: number
    failed: number
    read: number
  }
  by_channel: {
    push?: number
    email?: number
    sms?: number
  }
  success_rate: number // percentage (0-100)
  read_rate: number // percentage (0-100)
}

export interface SendNotificationResponse {
  notification_id: string
  deliveries_created: number
  channels_used: NotificationChannel[]
  scheduled: boolean
  sent_at?: string
  message: string
}

export interface RegisterDeviceTokenDto {
  device_id: string
  fcm_token: string
  platform: 'ios' | 'android' | 'web'
  app_version?: string
  employee_id?: string
}

export interface MyNotificationsFilters {
  is_read?: boolean
  type?: NotificationType
  from_date?: string
  to_date?: string
  page?: number
  limit?: number
}

// Helper functions

export function getNotificationTypeLabel(type: NotificationType): string {
  const labels: Record<NotificationType, string> = {
    [NotificationType.GENERAL]: 'Общее уведомление',
    [NotificationType.REMINDER]: 'Напоминание',
    [NotificationType.DOCUMENTS]: 'Документы',
    [NotificationType.REQUESTS]: 'Заявки',
  }
  return labels[type] || type
}

export function getNotificationPriorityLabel(priority: NotificationPriority): string {
  const labels: Record<NotificationPriority, string> = {
    [NotificationPriority.LOW]: 'Низкий',
    [NotificationPriority.NORMAL]: 'Обычный',
    [NotificationPriority.HIGH]: 'Высокий',
    [NotificationPriority.URGENT]: 'Срочный',
  }
  return labels[priority] || priority
}

export function getNotificationChannelLabel(channel: NotificationChannel): string {
  const labels: Record<NotificationChannel, string> = {
    [NotificationChannel.PUSH]: 'Push',
    [NotificationChannel.EMAIL]: 'Email',
    [NotificationChannel.SMS]: 'SMS',
  }
  return labels[channel] || channel
}

export function getDeliveryStatusLabel(status: DeliveryStatus): string {
  const labels: Record<DeliveryStatus, string> = {
    [DeliveryStatus.PENDING]: 'Ожидание',
    [DeliveryStatus.SENT]: 'Отправлено',
    [DeliveryStatus.DELIVERED]: 'Доставлено',
    [DeliveryStatus.FAILED]: 'Ошибка',
    [DeliveryStatus.READ]: 'Прочитано',
  }
  return labels[status] || status
}

export function getNotificationPriorityColor(priority: NotificationPriority): string {
  const colors: Record<NotificationPriority, string> = {
    [NotificationPriority.LOW]: 'bg-gray-100 text-gray-800',
    [NotificationPriority.NORMAL]: 'bg-blue-100 text-blue-800',
    [NotificationPriority.HIGH]: 'bg-orange-100 text-orange-800',
    [NotificationPriority.URGENT]: 'bg-red-100 text-red-800',
  }
  return colors[priority] || 'bg-gray-100 text-gray-800'
}

export function getDeliveryStatusColor(status: DeliveryStatus): string {
  const colors: Record<DeliveryStatus, string> = {
    [DeliveryStatus.PENDING]: 'bg-gray-100 text-gray-800',
    [DeliveryStatus.SENT]: 'bg-blue-100 text-blue-800',
    [DeliveryStatus.DELIVERED]: 'bg-green-100 text-green-800',
    [DeliveryStatus.FAILED]: 'bg-red-100 text-red-800',
    [DeliveryStatus.READ]: 'bg-purple-100 text-purple-800',
  }
  return colors[status] || 'bg-gray-100 text-gray-800'
}
